import toast from 'react-hot-toast';
import { ENDPOINTS } from '../../apis/endpoints';
import { apiSlice } from '../../app/api/apiSlice';
import { deleteDraft, reset, setGiftForms, setKras } from './giftSlice';

export const giftApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    checkEligibility: builder.query({
      query: () => `${ENDPOINTS.GIFT_FORM.CHECK_ELIGIBILITY}`,
      providesTags: ['GiftForm'],
      async onQueryStarted (args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setKras({ message: data.message, eligible: data.success, data: data.data }));
        } catch (err) {
          console.log(err);
          toast.error('An error occurred');
        }
      },
    }),
    getMyForms: builder.query({
      query: () => `${ENDPOINTS.GIFT_FORM.GET_MY_FORMS}`,
      providesTags: ['GiftForm'],
      async onQueryStarted (args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setGiftForms({ forms: data.data, key: 'my' }));
        } catch (err) {
          console.log(err);
          toast.error('An error occurred');
        }
      },
    }),
    getJuniorForms: builder.query({
      query: () => `${ENDPOINTS.GIFT_FORM.GET_JUNIOR_FORMS}`,
      providesTags: (result, error, args) => {
        let allForms = [];
        for (let i = 0; i < result?.data?.pending.length; i++) {
          allForms = [...allForms, ...result?.data?.pending[i]?.forms];
        }
        for (let i = 0; i < result?.data?.previous.length; i++) {
          allForms = [...allForms, ...result?.data?.previous[i]?.forms];
        }
        return allForms
          ? [{ type: 'GiftForm', id: 'LIST' }, ...allForms?.map(({ _id }) => ({ type: 'GiftForm', id: _id }))]
          : [{ type: 'GiftForm', id: 'LIST' }];
      },
      async onQueryStarted (args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setGiftForms({ forms: data.data, key: 'junior', replace: true }));
        } catch (err) {
          console.log(err);
          toast.error('An error occurred');
        }
      },
    }),
    getAllForms: builder.query({
      query: () => `${ENDPOINTS.GIFT_FORM.GET_ALL_FORMS}`,
      providesTags: (result, error, args) => {
        let allForms = [];
        for (let i = 0; i < result?.data?.pending.length; i++) {
          allForms = [...allForms, ...result?.data?.pending[i]?.forms];
        }
        for (let i = 0; i < result?.data?.previous.length; i++) {
          allForms = [...allForms, ...result?.data?.previous[i]?.forms];
        }
        return allForms
          ? [{ type: 'GiftForm', id: 'LIST' }, ...allForms?.map(({ _id }) => ({ type: 'GiftForm', id: _id }))]
          : [{ type: 'GiftForm', id: 'LIST' }];
      },
      async onQueryStarted (args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setGiftForms({ forms: data.data, key: 'all', replace: true }));
        } catch (err) {
          console.log(err);
          toast.error('An error occurred');
        }
      },
    }),
    submitForm: builder.mutation({
      query: body => ({
        url: `${ENDPOINTS.GIFT_FORM.CREATE_GIFT_FORM}`,
        method: 'POST',
        body: body,
      }),
      invalidatesTags: (result, error, body) => {
        let tags = !body.isDraft ? ['GiftForm'] : null;
        return tags;
      },
      async onQueryStarted (args, { dispatch, queryFulfilled }) {
        try {
          const { isDraft, response } = args;
          const { data } = await queryFulfilled;
          if (isDraft) {
            toast.success('Saved as draft');
          } else {
            !isDraft && dispatch(reset({ all: true }));
            toast.success(data.message);
          }
        } catch (err) {
          toast.error('An error occurred');
        }
      },
    }),
    changeStatus: builder.mutation({
      query: ({ id, body }) => ({
        url: `${ENDPOINTS.GIFT_FORM.BASE}/${id}/status`,
        method: 'PUT',
        body: body,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'GiftForm', id }],
    }),
    deleteForm: builder.mutation({
      query: id => ({
        url: `${ENDPOINTS.GIFT_FORM.BASE}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['GiftForm'],
      async onQueryStarted (args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(deleteDraft());
          toast.success(data.message);
        } catch (err) {
          toast.error('An error occurred');
        }
      },
    }),
    getAllEmployeesForJury: builder.query({
      query: () => ENDPOINTS.EMPLOYEE.BASE,
    }),
    getAllGiftInterviews: builder.query({
      query: () => ENDPOINTS.GIFT_FORM.GET_GIFT_INTERVIEWS,
      providesTags: ['GiftInterview'],
    }),
    submitFeedBack: builder.mutation({
      query: body => ({
        url: ENDPOINTS.GIFT_FORM.SUBMIT_FEEDBACK,
        method: 'PATCH',
        body: body,
      }),
      invalidatesTags: (result, error) =>
        result ? [{ type: 'GiftInterview', id: 'LIST' }] : [{ type: 'GiftInterview', id: 'LIST' }],
      async onQueryStarted (args, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          toast.success(data?.message || 'Feedback from submitted successfully');
        } catch (err) {
          toast.error(err?.message || err?.data?.message || 'Error submitting feedback');
        }
      },
    }),
    declareResult: builder.mutation({
      query: ({ id, body }) => ({
        url: `${ENDPOINTS.GIFT_FORM.BASE}/${id}/result`,
        method: 'POST',
        body: body,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'GiftForm', id }],
    }),
  }),
});

export const {
  useCheckEligibilityQuery,
  useSubmitFormMutation,
  useGetAllFormsQuery,
  useGetJuniorFormsQuery,
  useGetMyFormsQuery,
  useChangeStatusMutation,
  useDeleteFormMutation,
  useGetAllEmployeesForJuryQuery,
  useGetAllGiftInterviewsQuery,
  useSubmitFeedBackMutation,
  useDeclareResultMutation,
} = giftApiSlice;
